import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  IconButton,
  FormHelperText,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../contexts/tenant-context";
import { useSnackbar } from "../contexts/snackbar-context";
import {
  useBugLogService,
  BugCategory,
  BugPriority,
  BugStatus,
} from "../services/bugLogService";
import { FileUploadArea } from "../components/documents/upload/file-upload-area";

const CATEGORIES: BugCategory[] = [
  "Bug Report",
  "Feature Request",
  "Comment/General Feedback",
];
const PRIORITIES: { value: BugPriority; label: string; color: string }[] = [
  { value: "Low", label: "Low", color: "#4caf50" },
  { value: "Medium", label: "Medium", color: "#ff9800" },
  { value: "High", label: "High", color: "#f44336" },
  { value: "Critical", label: "Critical", color: "#9c27b0" },
];
const STATUSES: BugStatus[] = ["In Progress", "Dev Done", "Done", "UAT"];
const STATUS_COLORS: Record<BugStatus, string> = {
  "In Progress": "#ffb74d",
  "Dev Done": "#4fc3f7",
  Done: "#81c784",
  UAT: "#ffd166",
};

const ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.pdf,.txt,.xls,.xlsx";
const MAX_ATTACHMENT_SIZE_MB = 10;
const ALLOWED_ATTACHMENT_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".pdf",
  ".txt",
  ".xls",
  ".xlsx",
];
const BUG_LIST_REFRESH_KEY = "bugReportListRefreshToken";

const BugReportFormPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { selectedTenant } = useTenant();
  const { showSuccess } = useSnackbar();
  const { createBugLog, uploadBugLogAttachments } = useBugLogService();
  const returnPath =
    typeof location.state?.returnTo === "string"
      ? location.state.returnTo
      : "/dashboard";

  const user = accounts && accounts.length > 0 ? accounts[0] : null;

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<BugCategory | "">("");
  const [descriptionStepToProduce, setDescriptionStepToProduce] = useState("");
  const [priority, setPriority] = useState<BugPriority | "">("");
  const [status, setStatus] = useState<BugStatus>("In Progress");
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isSupportedAttachment = (file: File) => {
    const lowerName = file.name.toLowerCase();
    const hasAllowedExtension = ALLOWED_ATTACHMENT_EXTENSIONS.some((ext) =>
      lowerName.endsWith(ext)
    );
    const hasAllowedMimeType =
      file.type.startsWith("image/") ||
      file.type === "application/pdf" ||
      file.type === "text/plain" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    return hasAllowedExtension || hasAllowedMimeType;
  };

  const validateAttachment = (file: File): string | null => {
    if (!isSupportedAttachment(file)) {
      return "Please attach a supported file (PNG/JPG/GIF/WebP/HEIC/PDF/TXT/XLS/XLSX).";
    }

    if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
      return `Attachment must be ${MAX_ATTACHMENT_SIZE_MB}MB or smaller.`;
    }

    return null;
  };

  const handleAttachmentSelection = (files: FileList) => {
    const selected = Array.from(files);
    if (selected.length === 0) return;

    const nextFiles: File[] = [];
    for (const file of selected) {
      const validationError = validateAttachment(file);
      if (validationError) {
        setValidationErrors((prev) => ({ ...prev, attachment: validationError }));
        return;
      }
      nextFiles.push(file);
    }

    setValidationErrors((prev) => ({ ...prev, attachment: "" }));
    setAttachmentFiles((prev) => [...prev, ...nextFiles]);
  };

  const handleAttachmentSelect = (files: FileList) => {
    handleAttachmentSelection(files);
  };

  const handleAttachmentDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingAttachment(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAttachmentSelection(e.dataTransfer.files);
    }
  };

  const handleAttachmentDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingAttachment(true);
  };

  const handleAttachmentDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingAttachment(false);
    }
  };

  const clearAttachment = (index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    setValidationErrors((prev) => ({ ...prev, attachment: "" }));
  };

  const clearAllAttachments = () => {
    setAttachmentFiles([]);
    setValidationErrors((prev) => ({ ...prev, attachment: "" }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = "Title is required.";
    if (!category) errors.category = "Category is required.";
    if (!descriptionStepToProduce.trim()) errors.descriptionStepToProduce = "Description / steps to reproduce are required.";
    if (!priority) errors.priority = "Priority is required.";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    setSubmitting(true);
    try {
      const createdBug = await createBugLog({
        title: title.trim(),
        category: category as BugCategory,
        descriptionStepToProduce: descriptionStepToProduce.trim(),
        priority: priority as BugPriority,
        status,
        attachmentUrl: undefined,
      });

      if (attachmentFiles.length > 0) {
        await uploadBugLogAttachments(createdBug.id, attachmentFiles);
      }

      showSuccess("Bug created successfully.");
      const refreshToken = Date.now().toString();
      localStorage.setItem(BUG_LIST_REFRESH_KEY, refreshToken);

      // Delay before closing/navigating so the fetch connection can drain cleanly
      // and avoid triggering OperationCanceledException on the backend.
      setTimeout(() => {
        if (window.opener && !window.opener.closed) {
          try {
            // localStorage token notifies the opener via the storage event;
            // avoid location.assign so the opener does not fully reload.
            window.opener.focus();
          } catch {
            // Fallback below handles navigation if opener is not available.
          }
          window.close();
          return;
        }

        navigate(returnPath);
      }, 300);
    } catch (err: any) {
      setError(
        err?.message || "Failed to submit bug report. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#111" }}>
      {/* Minimal header */}
      <Box
        sx={{
          bgcolor: "#000",
          borderBottom: "1px solid #222",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <BugReportIcon sx={{ color: "hsl(19 101% 51% / 1)" }} />
        <Typography fontWeight={700} color="#fff" fontSize={18}>
          Report a Bug
        </Typography>
        {selectedTenant && (
          <Chip
            label={selectedTenant.tenantName}
            size="small"
            sx={{
              ml: "auto",
              bgcolor: "hsl(19 101% 51% / 0.15)",
              color: "hsl(19 101% 51% / 1)",
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        )}
      </Box>

      <Container maxWidth="md" sx={{ py: 5 }}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            bgcolor: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderRadius: 2,
            p: { xs: 3, md: 5 },
          }}
        >
          {submitting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 3,
                borderRadius: 2,
                bgcolor: "rgba(10,10,10,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress sx={{ color: "hsl(19 101% 51% / 1)" }} />
            </Box>
          )}

          {/* Reporter info banner */}
          {user && (
            <Box
              sx={{
                bgcolor: "#222",
                border: "1px solid #333",
                borderRadius: 1,
                px: 2,
                py: 1.5,
                mb: 3,
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Typography fontSize={13} color="#aaa">
                Logged by:{" "}
                <Box component="span" fontWeight={700} color="#fff">
                  {user.name || user.username}
                </Box>
              </Typography>
              {selectedTenant && (
                <Typography fontSize={13} color="#aaa">
                  Workspace:{" "}
                  <Box component="span" fontWeight={700} color="#fff">
                    {selectedTenant.tenantName}
                  </Box>
                </Typography>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate aria-busy={submitting}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                opacity: submitting ? 0.8 : 1,
              }}
            >
              {/* Title */}
              <TextField
                label="Bug Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={submitting}
                required
                fullWidth
                error={!!validationErrors.title}
                helperText={validationErrors.title}
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />

              {/* Category + Priority row */}
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  alignItems: "start",
                }}
              >
                <FormControl
                  required
                  error={!!validationErrors.category}
                  sx={{ minWidth: 0, ...selectFormSx }}
                >
                  <InputLabel sx={{ color: "#aaa" }}>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value as BugCategory)}
                    disabled={submitting}
                    sx={selectSx}
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.category && (
                    <FormHelperText>{validationErrors.category}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  required
                  error={!!validationErrors.priority}
                  sx={{ minWidth: 0, ...selectFormSx }}
                >
                  <InputLabel sx={{ color: "#aaa" }}>Priority</InputLabel>
                  <Select
                    value={priority}
                    label="Priority"
                    onChange={(e) => setPriority(e.target.value as BugPriority)}
                    disabled={submitting}
                    sx={selectSx}
                    renderValue={(val) =>
                      val ? (
                        <Box
                          component="span"
                          sx={{
                            color: PRIORITIES.find((p) => p.value === val)?.color,
                            fontWeight: 700,
                          }}
                        >
                          {val}
                        </Box>
                      ) : undefined
                    }
                  >
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        <Box
                          component="span"
                          sx={{ color: p.color, fontWeight: 700, mr: 1 }}
                        >
                          ●
                        </Box>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.priority && (
                    <FormHelperText>{validationErrors.priority}</FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ minWidth: 0, ...selectFormSx }}>
                  <InputLabel sx={{ color: "#aaa" }}>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value as BugStatus)}
                    disabled={submitting}
                    sx={selectSx}
                    renderValue={(val) => (
                      <Box
                        component="span"
                        sx={{
                          color: STATUS_COLORS[val as BugStatus],
                          fontWeight: 700,
                        }}
                      >
                        {val}
                      </Box>
                    )}
                  >
                    {STATUSES.map((statusOption) => (
                      <MenuItem key={statusOption} value={statusOption}>
                        <Box
                          component="span"
                          sx={{ color: STATUS_COLORS[statusOption], fontWeight: 700, mr: 1 }}
                        >
                          ●
                        </Box>
                        {statusOption}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Set the current progress for this report.</FormHelperText>
                </FormControl>
              </Box>

              {/* Description / steps */}
              <TextField
                label="Description / Steps to Produce"
                value={descriptionStepToProduce}
                onChange={(e) => setDescriptionStepToProduce(e.target.value)}
                disabled={submitting}
                required
                fullWidth
                multiline
                minRows={5}
                maxRows={12}
                error={!!validationErrors.descriptionStepToProduce}
                helperText={
                  validationErrors.descriptionStepToProduce ||
                  "Include what happened and how to reproduce it."
                }
                inputProps={{ maxLength: 3000 }}
                sx={inputSx}
              />

              {/* Attach image / screenshot */}
              <Box>
                <Typography fontSize={13} color="#aaa" mb={1}>
                  Attach File (Image, PDF, TXT, Excel) (Optional)
                </Typography>

                <FileUploadArea
                  isDragging={isDraggingAttachment}
                  onDrop={handleAttachmentDrop}
                  onDragOver={handleAttachmentDragOver}
                  onDragLeave={handleAttachmentDragLeave}
                  onFileSelect={handleAttachmentSelect}
                  primaryText="Attach images, PDF, text, or Excel files for this bug report."
                  secondaryText="Drag and drop files, or click to browse."
                  dragText="Drag & drop attachments here"
                  dropText="Drop attachments here"
                  browseText="or click to browse files"
                  accept={ATTACHMENT_ACCEPT}
                  multiple={true}
                  maxInfoLabel={`Max ${MAX_ATTACHMENT_SIZE_MB}MB per file`}
                  disabled={submitting}
                />

                {attachmentFiles.length > 0 && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={clearAllAttachments}
                      disabled={submitting}
                      sx={{ alignSelf: "flex-end", color: "#ff8a65", textTransform: "none" }}
                    >
                      Remove all
                    </Button>
                    {attachmentFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          border: "1px solid #333",
                          borderRadius: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" color="#fff" fontWeight={600}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="#bbb">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => clearAttachment(index)}
                          disabled={submitting}
                          sx={{
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(200,50,50,0.8)" },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {validationErrors.attachment && (
                  <FormHelperText error sx={{ mt: 0.75 }}>
                    {validationErrors.attachment}
                  </FormHelperText>
                )}
              </Box>

              <Divider sx={{ bgcolor: "#2a2a2a" }} />

              {/* Submit */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(returnPath)}
                  disabled={submitting}
                  sx={{
                    borderColor: "#444",
                    color: "#aaa",
                    textTransform: "none",
                    "&:hover": { borderColor: "#666", color: "#fff" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <BugReportIcon />
                    )
                  }
                  sx={{
                    bgcolor: "hsl(19 101% 51% / 1)",
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "none",
                    px: 3,
                    "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" },
                    "&:disabled": { bgcolor: "hsl(19 101% 51% / 0.4)", color: "#fff" },
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Bug Report"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// Shared MUI style helpers for dark theme inputs
const inputSx = {
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 0.6)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 1)",
  },
  "& .MuiInputLabel-root": { color: "#aaa" },
  "& .MuiInputLabel-root.Mui-focused": { color: "hsl(19 101% 51% / 1)" },
  "& .MuiFormHelperText-root": { color: "#666" },
};

const selectFormSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 0.6)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 1)",
  },
};

const selectSx = {
  color: "#fff",
  "& .MuiSvgIcon-root": { color: "#aaa" },
};

export default BugReportFormPage;
